import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from "react";
import { fetchPoolSaved, removeFromPool, saveToPool } from "../../api/candidate-pool";
import { useGlobalState } from "../globalState";

/**
 * "Save to pool" on the person page: saves the person into the candidate pool
 * of the viewer's own group, or removes that save. Staff only, like the pool.
 */
export const SaveToPoolButton = ({ personId }: { personId: number }) => {
	const { currentUser } = useGlobalState();
	const group = currentUser?.userprofile?.up_group?.id ?? null;
	const allowed = !!(currentUser?.is_staff || currentUser?.is_superuser);
	const [saved, setSaved] = useState<boolean | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!allowed || group === null) return;
		setSaved(null);
		fetchPoolSaved(group, personId)
			.then((answer) => setSaved(answer.saved))
			.catch((e: Error) => setError(e.message));
	}, [allowed, group, personId]);

	if (!allowed || group === null) return null;

	const toggle = () => {
		setBusy(true);
		setError(null);
		(saved ? removeFromPool(group, personId) : saveToPool(group, personId))
			.then(() => setSaved(!saved))
			.catch((e: Error) => setError(e.message))
			.finally(() => setBusy(false));
	};

	return (
		<Tooltip
			title={
				error
					? `Candidate pool: ${error}`
					: saved
						? "Saved to the candidate pool. Click to remove the save."
						: "Keep this person in the candidate pool"
			}
			data-component="save-to-pool-tooltip"
		>
			<span>
				<Button
					variant="outlined"
					size="small"
					color={error ? "error" : "primary"}
					startIcon={saved ? <BookmarkAddedIcon /> : <BookmarkAddIcon />}
					disabled={saved === null || busy}
					onClick={toggle}
					data-component="save-to-pool-button"
				>
					{saved ? "Saved to pool" : "Save to pool"}
				</Button>
			</span>
		</Tooltip>
	);
};
